// Storage facade — single import surface for the rest of the app.
//
// The interface is defined here. Backends (IndexedDB now, R2 later) implement
// the same shape and are injected via `setBackend()`. Callers never import a
// backend directly — they import from this module.
//
// Filename / path conventions (§5 of CLAUDE.md) are computed here as PURE
// functions so they're identical across backends and survive a backend swap.

export const SCHEMA_VERSION = '2026.05.1';

let _backend = null;

export function setBackend(backend) {
  _backend = backend;
}

function be() {
  if (!_backend) throw new Error('Storage backend not initialised. Call setBackend() first.');
  return _backend;
}

// ---- Data API ----

export const savePhoto      = (blob, meta) => be().savePhoto(blob, meta);
export const loadPhoto      = (file)       => be().loadPhoto(file);
export const listPhotos     = (date)       => be().listPhotos(date);
export const deletePhoto    = (file)       => be().deletePhoto(file);
export const saveJournal    = (date, j)    => be().saveJournal(date, j);
export const loadJournal    = (date)       => be().loadJournal(date);
export const listEntryDates = (range = {}) => be().listEntryDates(range);

// ---- Session-key API (set on unlock, cleared on lock) ----

export const setSessionKey   = (key) => be().setSessionKey(key);
export const clearSessionKey = ()    => be().clearSessionKey();
export const hasSessionKey   = ()    => be().hasSessionKey();

// ---- Config API (auth material, parent name, autoLockMs, ...) ----

export const getConfig    = (key)        => be().getConfig(key);
export const setConfig    = (key, value) => be().setConfig(key, value);
export const deleteConfig = (key)        => be().deleteConfig(key);

// ---- Maintenance ----

export const migrateAllToEncrypted = () => be().migrateAllToEncrypted();
export const wipeAll               = () => be().wipeAll();

// ---- Pure helpers (path / filename conventions, §5) ----

/**
 * Build the canonical photo filename and date.
 * Format: photos/YYYY-MM-DD/YYYY-MM-DDTHHMM_<profile>_v<n>.jpg
 *
 * Both IndexedDB (Phase 1) and R2 (Phase 2) use this string as the key, so the
 * dataset is portable across backends with zero rewrites.
 */
export function buildPhotoFilename({ takenAt, profile, version = 1 }) {
  const d = takenAt instanceof Date ? takenAt : new Date(takenAt);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const HH = String(d.getHours()).padStart(2, '0');
  const MM = String(d.getMinutes()).padStart(2, '0');
  const date = `${yyyy}-${mm}-${dd}`;
  const file = `photos/${date}/${date}T${HH}${MM}_${profile}_v${version}.jpg`;
  return { date, file };
}

/** Build the canonical journal path for a date. */
export function buildJournalPath(date) {
  return `journal/${date}.json`;
}

/** Today's date as YYYY-MM-DD in the device's local timezone. */
export function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
